import { parseWorkflowNodeData } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';

const { healWorkflowBindingSources, migrateDatasetWriteRowKeyBindings } =
  PersistedWorkflow;

/** Extra display names a binding ref may have used, keyed by node id. */
export type WorkflowNodeSourceAliases = ReadonlyMap<string, readonly string[]>;

type HealableGraph = {
  nodes: Array<{ id: string; type: string; data?: unknown }>;
};

function readAgentId(data: unknown): string | undefined {
  const parsed = parseWorkflowNodeData('agent', data);
  if (parsed.t === 'invalid' || parsed.data.agentId === '') {
    return undefined;
  }
  return parsed.data.agentId;
}

/**
 * An agent's title is the display name the server hands Coco for that node, but
 * it lives on the agent rather than the graph — so the editor has to supply it
 * once the agent list has loaded.
 */
export function agentTitleAliasesForGraph(
  graph: HealableGraph,
  agentTitleById: ReadonlyMap<string, string>
): WorkflowNodeSourceAliases {
  const aliases = new Map<string, readonly string[]>();
  for (const node of graph.nodes) {
    if (node.type !== 'agent') {
      continue;
    }
    const agentId = readAgentId(node.data);
    const title =
      agentId === undefined ? undefined : agentTitleById.get(agentId);
    if (title !== undefined && title.trim() !== '') {
      aliases.set(node.id, [title]);
    }
  }
  return aliases;
}

/**
 * Repairs binding refs that name an upstream node instead of referencing its
 * id. Graphs Coco authored before it was taught to bind by id are otherwise
 * unsaveable — the save endpoint rejects the whole graph when any ref names a
 * source it can't find among the node ids — and those refs resolve to nothing
 * at run time regardless.
 *
 * Returns the graph by identity when nothing needed fixing, so callers can skip
 * a re-render (and so draft/baseline stay reference-equal on healthy graphs).
 */
export function healWorkflowGraphBindingSources<TGraph extends HealableGraph>(
  graph: TGraph,
  extraAliasesByNodeId?: WorkflowNodeSourceAliases
): TGraph {
  const { nodes, repairs } = healWorkflowBindingSources({
    nodes: graph.nodes,
    ...(extraAliasesByNodeId !== undefined && { extraAliasesByNodeId }),
  });
  // Also migrate a legacy dataset-write row identifier from `rows[].rowId` to
  // `rows[].rowKey` (see migrateDatasetWriteRowKeyBindings): binding by row key
  // is what makes a write-back resolve on a business-keyed dataset. Opening the
  // workflow is what persists it, so it rides the same heal-on-load path.
  const { nodes: migrated, migrations } =
    migrateDatasetWriteRowKeyBindings(nodes);
  if (repairs.length === 0 && migrations.length === 0) {
    return graph;
  }
  return { ...graph, nodes: [...migrated] };
}
